import React, { useEffect, useState } from "react";
import ReactECharts from "echarts-for-react";
import { Box, Stack, Grid } from "@mui/material";
import VSNTypography from "../../../shared/VSNTypography";
import useOTBStyle from "../OTBStyle";
import PaiChartComponent from "./PaiChartComponent";
import { DataSummary } from "../../assessmentsView/physicalAssessment/PhysicalDashboardPDFCommonComponent";
import { capitalizeFirstLetter } from "../../../utils/javascript";

const Demographics = ({ data, pdf }) => {
  const classes = useOTBStyle();
  const [selectedEthnicity, setSelectedEthnicity] = useState(null);
  const [selectedGender, setSelectedGender] = useState(null);
  const [selectedAgeGroup, setSelectedAgeGroup] = useState(null);

  const filteredData = data?.filter((item) => {
    return (
      (!selectedEthnicity || item.ethnicity === selectedEthnicity) &&
      (!selectedGender || item.gender === selectedGender) &&
      (!selectedAgeGroup ||
        (item.age >= selectedAgeGroup.min && item.age <= selectedAgeGroup.max))
    );
  });

  const groupBy = (key) =>
    filteredData?.reduce((acc, item) => {
      const value = item[key];
      acc[value] = acc[value] || [];
      acc[value].push(item);
      return acc;
    }, {});

  const ethnicityData = Object.entries(groupBy("ethnicity") || {})
    ?.map(([key, items]) => ({
      name: key,
      value: items.length,
    }))
    .filter((item) => item.name !== "null");

  // Gender Data
  const genderData = Object.entries(groupBy("gender") || {})
    ?.map(([key, items]) => ({
      name: capitalizeFirstLetter(key),
      value: items.length,
    }))
    .filter((item) => item.name !== "null");

  // Age Group Data
  const ageGroups = ["20-29", "30-39", "40-49", "50+"];
  const ageGroupData = ageGroups?.map((group) => {
    const [min, max] =
      group === "20-29"
        ? [20, 29]
        : group === "30-39"
        ? [30, 39]
        : group === "40-49"
        ? [40, 49]
        : [50, Infinity];
    const count = filteredData?.filter(
      (item) => item.age >= min && item?.age <= max
    ).length;
    return { name: group, value: count };
  });

  // Scatter Data for Age vs. Service Years
  const scatterData = filteredData.map((item) => [
    item?.age,
    item?.service_year?.result,
  ]);

  // Handle click events on the charts
  const handleEthnicityClick = (e) => {
    setSelectedEthnicity((prev) => (prev === e.name ? null : e.name));
  };

  const handleGenderClick = (e) => {
    setSelectedGender((prev) => (prev === e.name ? null : e.name));
  };

  const handleAgeGroupClick = (e) => {
    const [min, max] =
      e.name === "20-29"
        ? [20, 29]
        : e.name === "30-39"
        ? [30, 39]
        : e.name === "40-49"
        ? [40, 49]
        : [50, Infinity];
    setSelectedAgeGroup((prev) =>
      prev && prev.min === min && prev.max === max ? null : { min, max }
    );
  };

  return (
    <>
      {pdf && (
        <Box sx={{ pageBreakAfter: "always" }}>
          <Box className={classes.pdfReportLayout}>
            <VSNTypography variant="h4">Demographics</VSNTypography>

            <Stack gap={1}>
              <Stack direction="row" gap={2}>
                {/* <DataSummary title="Ethnicity" data={data} field="ethnicity" /> */}
                <PaiChartComponent
                  data={ethnicityData}
                  height="350px"
                  width="530px"
                  color={[
                    "#C3A577",
                    "#E0BE94",
                    "#E8D0B1",
                    "#F1E1CD",
                    "#B3B3B3",
                  ]}
                  pdf
                  title="Ethnicity"
                />

                <PaiChartComponent
                  data={genderData}
                  height="350px"
                  width="530px"
                  color={[
                    "#C3A577",
                    "#E0BE94",
                    "#E8D0B1",
                    "#F1E1CD",
                    "#B3B3B3",
                  ]}
                  pdf
                  title="Gender"
                />
              </Stack>

              <Stack direction="row" gap={2}>
                <ReactECharts
                  option={{
                    title: { text: "Age Group", bottom: "5%" },
                    xAxis: { type: "category", data: ageGroups },
                    yAxis: { type: "value" },
                    tooltip: { trigger: "item" },
                    color: [
                      "#C3A577",
                      "#E0BE94",
                      "#E8D0B1",
                      "#F1E1CD",
                      "#B3B3B3",
                    ],
                    series: [
                      {
                        name: "Age Group",
                        type: "bar",

                        label: {
                          show: true,
                          position: "top",
                          textStyle: {
                            fontWeight: "bold",
                            fontSize: 14,
                          },
                        },
                        animation: false,
                        animationDuration: 0,
                        animationDurationUpdate: 0,
                        data: ageGroupData?.map((item) => item.value),
                      },
                    ],
                  }}
                  style={{ height: "350px", width: "530px" }}
                />
                <ReactECharts
                  option={{
                    title: { text: "Age vs Service Years", bottom: "5%" },
                    xAxis: { type: "value", name: "Age" },
                    yAxis: { type: "value", name: "Service Years" },
                    tooltip: { trigger: "item" },
                    color: [
                      "#C3A577",
                      "#E0BE94",
                      "#E8D0B1",
                      "#F1E1CD",
                      "#B3B3B3",
                    ],
                    series: [
                      {
                        name: "Age vs Service Years",
                        type: "scatter",
                        data: scatterData,
                        animation: false,
                        animationDuration: 0,
                        animationDurationUpdate: 0,
                      },
                    ],
                  }}
                  style={{ height: "350px", width: "530px" }}
                />
              </Stack>
            </Stack>
          </Box>
        </Box>
      )}

      {!pdf && (
        <Box>
          <Box className={classes.reportLayout} pb={4}>
            <div className={classes.stickyTitle}>
              <VSNTypography variant="h2">Demographics</VSNTypography>
            </div>

            <Grid container spacing={2}>
              <Grid item xs={12} smd={6}>
                <Box maxWidth={200} mx="auto">
                  <DataSummary
                    title="Ethnicity"
                    data={data}
                    field="ethnicity"
                  />
                </Box>
                <Box sx={{ height: { smd: 450, xs: 400 } }}>
                  <PaiChartComponent
                    data={ethnicityData}
                    height="450px"
                    width="100%"
                    color={[
                      "#C3A577",
                      "#E0BE94",
                      "#E8D0B1",
                      "#F1E1CD",
                      "#B3B3B3",
                    ]}
                    // onEvents={{
                    //   click: handleEthnicityClick,
                    // }}
                  />
                </Box>
              </Grid>

              <Grid item xs={12} smd={6}>
                <Box maxWidth={200} mx="auto">
                  <DataSummary title="Gender" data={data} field="gender" />
                </Box>
                <Box sx={{ height: { smd: 450, xs: 400 } }}>
                  <PaiChartComponent
                    data={genderData}
                    height="450px"
                    width="100%"
                    color={[
                      "#C3A577",
                      "#E0BE94",
                      "#E8D0B1",
                      "#F1E1CD",
                      "#B3B3B3",
                    ]}
                    // onEvents={{
                    //   click: handleGenderClick,
                    // }}
                  />
                </Box>
              </Grid>
            </Grid>
            <Grid container spacing={2} mt={3}>
              <Grid item xs={12} smd={6}>
                <Box sx={{ height: { lg: 500, smd: 450, xs: 400 } }}>
                  <ReactECharts
                    option={{
                      title: { text: "Age Group", left: "center" },
                      xAxis: {
                        type: "category",
                        data: ageGroups,
                        axisLine: {
                          show: true,
                          lineStyle: {
                            color: "#6E7079",
                            width: 1,
                          },
                        },
                      },
                      yAxis: {
                        type: "value",
                        axisLine: {
                          show: true,
                          lineStyle: {
                            color: "#6E7079",
                            width: 1,
                          },
                        },
                      },
                      tooltip: { trigger: "item" },
                      color: [
                        "#C3A577",
                        "#E0BE94",
                        "#E8D0B1",
                        "#F1E1CD",
                        "#B3B3B3",
                      ],
                      series: [
                        {
                          name: "Age Group",
                          type: "bar",
                          data: ageGroupData.map((item) => item.value),
                          animation: false,
                          animationDuration: 0,
                          animationDurationUpdate: 0,
                        },
                      ],
                    }}
                    // onEvents={{
                    //   click: handleAgeGroupClick,
                    // }}
                    style={{ height: "400px", width: "100%" }}
                  />
                </Box>
              </Grid>

              <Grid item xs={12} smd={6}>
                <Box sx={{ height: { lg: 500, smd: 450, xs: 400 } }}>
                  <ReactECharts
                    option={{
                      title: { text: "Age vs Service Years", left: "center" },
                      xAxis: { type: "value", name: "Age" },
                      yAxis: { type: "value", name: "Service Years" },
                      tooltip: { trigger: "item" },
                      color: [
                        "#C3A577",
                        "#E0BE94",
                        "#E8D0B1",
                        "#F1E1CD",
                        "#B3B3B3",
                      ],
                      series: [
                        {
                          name: "Age vs Service Years",
                          type: "scatter",
                          data: scatterData,
                          animation: false,
                          animationDuration: 0,
                          animationDurationUpdate: 0,
                        },
                      ],
                    }}
                    style={{ height: "400px", width: "100%" }}
                  />
                </Box>
              </Grid>
            </Grid>
          </Box>
        </Box>
      )}
    </>
  );
};

export default Demographics;
